import { fetchWithSession, sessionForToken } from "./sessions.js";
import {
  LIVE_BOARD_PATHS,
  DM_ALL_MESSAGES_PATH,
  AUTH_STORAGE_KEY,
  TIMELINE_PAGE_SIZE,
} from "./config.js";
import { effectiveStatus, statusConversationRootId } from "./catalog.js";

const STATUS_FETCH_CACHE = new Map();      // id -> status|null
const STATUS_FETCH_INFLIGHT = new Map();   // id -> Promise<status|null>
const STATUS_FETCH_CACHE_MAX = 2000;

function _readStoredApiBase() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    // Multi-account shape: { accounts: [...], activeId } — read the active
    // session's apiBase.
    if (stored && Array.isArray(stored.accounts)) {
      const active = stored.accounts.find((a) => a && a.id === stored.activeId);
      const apiBase = active?.apiBase;
      return apiBase !== undefined ? apiBase : null;
    }
    // Legacy single-session shape: { accessToken, account, instance, apiBase }.
    // apiBase may be "" (same-origin), "https://..." (remote), or missing.
    return stored.hasOwnProperty("apiBase") ? stored.apiBase : null;
  } catch { return null; }
}

// Resolve the API base for a request. An explicit `apiBase` (threaded from
// state.auth.apiBase via the app.js apiFetch wrapper) wins; otherwise fall back
// to the active session in localStorage. The explicit value matters during an
// account switch / add-account, when state already holds the new instance but
// localStorage hasn't been persisted yet.
function resolveApiBase(apiBase) {
  if (apiBase !== undefined) return apiBase;
  return _readStoredApiBase();
}

function apiUrl(path, apiBase) {
  const base = resolveApiBase(apiBase);
  // null means no signed-in instance; fall back to same-origin.
  return (base !== null ? base : "") + path;
}

// Whether it's safe to attach the bearer token to a request for `url`. The
// token must only ever go to the signed-in instance's origin (or same-origin
// when proxied) — never to an arbitrary host. This is the last line of defense
// against a token-exfiltration vector if some caller is ever pointed off-base.
function isAuthorizedTarget(url, apiBase) {
  let target;
  try { target = new URL(url, location.origin); } catch { return false; }
  const base = resolveApiBase(apiBase);
  if (!base) return target.origin === location.origin;  // same-origin only
  try { return target.origin === new URL(base).origin; } catch { return false; }
}

// Wrapper around fetch that adds the Authorization header when a token is
// present. Returns a Response (caller decides whether to check .ok or .json()).
// `apiBase` is threaded from state by the app.js apiFetch wrapper so that an
// request stays bound to its instance across account switches. Callers without
// an explicit base use the stored session matching their token.
export function apiFetch(path, opts = {}, accessToken = null, apiBase = undefined) {
  // Capture the token and its instance together before yielding. Never combine
  // a request's old token with whichever account is now active in storage.
  const session = sessionForToken(accessToken, apiBase);
  const base = apiBase !== undefined ? apiBase : session ? session.apiBase : resolveApiBase(apiBase);
  const url = apiUrl(path, base);
  const authorized = accessToken && isAuthorizedTarget(url, base);
  const send = (token) => {
    const headers = { ...(opts.headers || {}) };
    if (authorized) headers["Authorization"] = `Bearer ${token}`;
    // Bypass browser and server URL caches (including Mitra favourites).
    const sep = url.includes("?") ? "&" : "?";
    return fetch(`${url}${sep}_=${Date.now()}`, { ...opts, headers, cache: "no-store" });
  };
  // Only automatically replay reads; posting/uploading must not be duplicated.
  return fetchWithSession(send, authorized ? session : null, accessToken, {
    retry: ["GET", "HEAD"].includes((opts.method || "GET").toUpperCase()),
  });
}

function cacheStatus(id, status) {
  STATUS_FETCH_CACHE.set(id, status || null);
  if (STATUS_FETCH_CACHE.size > STATUS_FETCH_CACHE_MAX) {
    const oldestKey = STATUS_FETCH_CACHE.keys().next().value;
    STATUS_FETCH_CACHE.delete(oldestKey);
  }
}

// Merge fresh fields into a cached status, if we hold one. The cache has no
// TTL — a status fetched once (to hydrate a thread OP the timeline omitted)
// is reused for the rest of the session — so an interaction made elsewhere in
// the app has to be written through, or the catalog keeps rendering the
// snapshot taken before the like/repost.
export function patchCachedStatus(id, patch) {
  if (!id || !patch) return;
  const cached = STATUS_FETCH_CACHE.get(id);
  if (!cached) return;
  Object.assign(cached, patch);
}

// Mark statuses as gone for the rest of the session, after a delete. The
// cache has no TTL, so without this a deleted post is handed straight back to
// the next thread-ancestor hydration that asks for it — and reappears in the
// catalog it was just removed from. Recorded as a known miss rather than
// simply dropped, so nothing goes looking for it again.
export function forgetCachedStatuses(ids) {
  for (const id of ids || []) {
    if (id) STATUS_FETCH_CACHE.set(id, null);
  }
}

async function fetchStatusById(id, accessToken, apiBase) {
  if (!id) return null;

  if (STATUS_FETCH_CACHE.has(id)) {
    return STATUS_FETCH_CACHE.get(id);
  }
  if (STATUS_FETCH_INFLIGHT.has(id)) {
    return STATUS_FETCH_INFLIGHT.get(id);
  }

  const p = (async () => {
    const r = await apiFetch(`/api/v1/statuses/${encodeURIComponent(id)}`, {}, accessToken, apiBase);
    if (!r.ok) {
      // Cache 404s (permanent missing) but avoid caching auth/transient failures.
      if (r.status === 404) cacheStatus(id, null);
      return null;
    }
    const st = await r.json();
    cacheStatus(id, st);
    return st;
  })().finally(() => {
    STATUS_FETCH_INFLIGHT.delete(id);
  });

  STATUS_FETCH_INFLIGHT.set(id, p);
  return p;
}

// ---- plural status fetch (GET /api/v1/statuses?id[]=…) ---------------------
//
// Hydrating a catalog page's missing thread OPs used to cost one request per
// OP — 28 on a measured page, capped at 200. The plural endpoint answers the
// whole set at once. A server without the route answers 404 (or 405, since
// /v1/statuses exists there for POST), and we fall back to per-id fetches for
// the session.
//
// Mitra >= v5.8.0 accepts up to 200 ids per request (and rejects the whole
// batch if any id is malformed), so ids are chunked and only ever come from
// server-issued `in_reply_to_id` values. Exported so the tests chunk against
// the real value rather than a copy that can drift out of sync with it.
export const PLURAL_STATUS_CHUNK = 200;
const PLURAL_STATUS_SUPPORT = new Map();   // apiBase -> boolean

export function supportsPluralStatusFetch(apiBase) {
  return PLURAL_STATUS_SUPPORT.get(apiBase !== undefined && apiBase !== null ? String(apiBase) : "");
}

function chunk(items, size) {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

// Fetch one chunk (≤ PLURAL_STATUS_CHUNK ids). Returns an array of statuses,
// or null if this instance has no plural endpoint.
async function fetchStatusChunk(ids, accessToken, apiBase) {
  const query = ids.map((id) => `id[]=${encodeURIComponent(id)}`).join("&");
  const r = await apiFetch(`/api/v1/statuses?${query}`, {}, accessToken, apiBase);
  if (r.status === 404 || r.status === 405) return null;
  if (!r.ok) throw new Error(`plural status fetch failed: HTTP ${r.status}`);
  const body = await r.json();
  return Array.isArray(body) ? body : [];
}

// Fetch many statuses by id, preferring the plural endpoint. Honours the same
// cache as fetchStatusById in both directions: ids already held are never
// re-requested, and everything fetched is cached for later pages.
//
// Ids the server omits are cached as "missing" (null), matching how a per-id
// 404 is cached. That covers both deleted posts and posts this viewer can't
// see; without it, every catalog page would re-request the same invisible OPs
// — the flood this exists to remove.
async function fetchStatusesByIds(ids, accessToken, apiBase) {
  const results = new Map();
  const wanted = [];
  for (const id of ids) {
    if (!id) continue;
    if (STATUS_FETCH_CACHE.has(id)) {
      results.set(id, STATUS_FETCH_CACHE.get(id));
      continue;
    }
    wanted.push(id);
  }
  if (!wanted.length) return results;

  const supportKey = apiBase !== undefined && apiBase !== null ? String(apiBase) : "";
  if (PLURAL_STATUS_SUPPORT.get(supportKey) !== false) {
    try {
      const chunks = await Promise.all(
        chunk(wanted, PLURAL_STATUS_CHUNK).map((part) => fetchStatusChunk(part, accessToken, apiBase)),
      );
      // A single chunk reporting "no such route" settles it for the instance.
      if (!chunks.includes(null)) {
        PLURAL_STATUS_SUPPORT.set(supportKey, true);
        for (const status of chunks.flat()) {
          if (!status?.id) continue;
          cacheStatus(status.id, status);
          results.set(status.id, status);
        }
        for (const id of wanted) {
          if (results.has(id)) continue;
          cacheStatus(id, null);
          results.set(id, null);
        }
        return results;
      }
      PLURAL_STATUS_SUPPORT.set(supportKey, false);
    } catch {
      // Transient failure: fall through to per-id fetches rather than marking
      // the instance unsupported on what may be a one-off error.
    }
  }

  const got = await Promise.all(wanted.map(async (id) => {
    try {
      return [id, await fetchStatusById(id, accessToken, apiBase)];
    } catch {
      return [id, null];
    }
  }));
  for (const [id, status] of got) results.set(id, status);
  return results;
}

// Compatibility path for servers that don't expose conversation.root_id.
// Every ancestor of `id` arrives in one request, avoiding a round trip for
// every level of a deep reply chain. Returns [] on any failure; the caller
// degrades to a shallower catalog rather than an error.
async function fetchAncestors(id, accessToken, apiBase) {
  try {
    const r = await apiFetch(`/api/v1/statuses/${encodeURIComponent(id)}/context`, {}, accessToken, apiBase);
    if (!r.ok) return [];
    const body = await r.json();
    const ancestors = Array.isArray(body?.ancestors) ? body.ancestors : [];
    for (const st of ancestors) {
      if (st?.id) cacheStatus(st.id, st);
    }
    return ancestors;
  } catch {
    return [];
  }
}

async function hydrateMissingThreadAncestors(statuses, accessToken, apiBase) {
  // Home timeline often includes followed users' replies/boosts while omitting
  // the OP if the OP author isn't followed. New Mitra statuses name that OP
  // directly as conversation.root_id, so fetch all missing roots in one
  // batched pass. Statuses from older servers fall back to the ancestor walk
  // below, making this safe during rolling upgrades and with mixed payloads.
  const byId = new Map();
  for (const row of statuses || []) {
    const st = effectiveStatus(row);
    if (st?.id) byId.set(st.id, st);
  }

  const fetched = [];
  const absorb = (st) => {
    if (!st?.id || byId.has(st.id)) return;
    byId.set(st.id, st);
    fetched.push(st);
  };

  const rootIds = new Set();
  const toFetch = new Set();
  for (const row of statuses || []) {
    const st = effectiveStatus(row);
    const rootId = statusConversationRootId(st);
    if (rootId) {
      if (!byId.has(rootId)) rootIds.add(rootId);
    } else if (st?.in_reply_to_id && !byId.has(st.in_reply_to_id)) {
      toFetch.add(st.in_reply_to_id);
    }
  }

  if (rootIds.size) {
    const roots = await fetchStatusesByIds(Array.from(rootIds), accessToken, apiBase);
    for (const root of roots.values()) absorb(root);
  }

  // Legacy fallback: statuses without conversation.root_id still have to
  // climb in_reply_to_id. Breadth is batched and deep chains switch to
  // /context after two parent rounds.
  const FETCH_CAP = 200; // safety valve against pathological deep/branchy threads
  // Breadth is cheap (one batched request per 200 ids); depth is not (one round
  // trip per level). Most chains are a level or two, so walk those, then hand
  // whatever is still climbing to the context endpoint, which returns a whole
  // chain at once. Measured on a real /home/ page: 22 chains, all but five of
  // them one level deep — and one 91 levels deep, which alone cost 91 round
  // trips before this.
  //
  // A chain that never reaches its root is worse than an expensive one: the
  // catalog drops any thread whose OP it can't see, so the card is missing
  // until some later page happens to complete the chain — at which point it
  // appears carrying the *earlier* page's activity as its bump, and lands near
  // the top of a list the reader is already scrolling. Hence the budget below
  // counts requests, not statuses: the walk's status cap must not be able to
  // stop the step that terminates a chain.
  const MAX_PARENT_ROUNDS = 2;
  const DEEP_CHAIN_CAP = 40;   // context calls issued for still-climbing chains
  const DEEP_CHAIN_PASSES = 2; // in case one context response stops short of the root

  let rounds = 0;
  while (toFetch.size && fetched.length < FETCH_CAP && rounds < MAX_PARENT_ROUNDS) {
    rounds++;
    const batch = Array.from(toFetch);
    toFetch.clear();

    const got = await fetchStatusesByIds(batch, accessToken, apiBase);

    for (const st of got.values()) {
      if (!st?.id || byId.has(st.id)) continue;
      absorb(st);
      const rootId = statusConversationRootId(st);
      if (rootId && !byId.has(rootId)) {
        toFetch.add(rootId);
      } else if (!rootId && st.in_reply_to_id && !byId.has(st.in_reply_to_id)) {
        toFetch.add(st.in_reply_to_id);
      }
    }
  }

  for (let pass = 0; pass < DEEP_CHAIN_PASSES && toFetch.size; pass++) {
    const deep = Array.from(toFetch).slice(0, DEEP_CHAIN_CAP);
    toFetch.clear();
    // The chains' own heads still have to be fetched — context returns what's
    // *above* a post, not the post itself — but that's one batched request for
    // all of them, issued alongside the per-chain context calls.
    const [heads, ...chains] = await Promise.all([
      fetchStatusesByIds(deep, accessToken, apiBase),
      ...deep.map((id) => fetchAncestors(id, accessToken, apiBase)),
    ]);
    for (const st of heads.values()) absorb(st);
    for (const chain of chains) {
      for (const st of chain) absorb(st);
    }
    // Anything still short of its root goes round again (a server that caps
    // the ancestors it returns would land here; Mitra doesn't).
    for (const id of deep) {
      const top = byId.get(id);
      if (top?.in_reply_to_id && !byId.has(top.in_reply_to_id)) toFetch.add(top.in_reply_to_id);
    }
    for (const chain of chains) {
      const top = chain[0];
      if (top?.in_reply_to_id && !byId.has(top.in_reply_to_id)) toFetch.add(top.in_reply_to_id);
    }
  }

  if (!fetched.length) return statuses;
  return [...statuses, ...fetched];
}

function appendMaxId(path, maxId) {
  if (!maxId) return path;
  return path + (path.includes("?") ? "&" : "?") + "max_id=" + encodeURIComponent(maxId);
}

// Pull the `max_id` cursor out of a Mastodon-style Link header's rel="next"
// entry, e.g.
//   Link: <https://host/api/v1/favourites?max_id=42>; rel="next", <…>; rel="prev"
// Required for /favourites, whose cursor is the favourite-record id (not the
// status id), so it can't be derived from the returned statuses. Returns null
// when there's no next page — or when the header isn't readable (cross-origin
// responses only expose Link with Access-Control-Expose-Headers).
function parseLinkNextMaxId(linkHeader) {
  if (!linkHeader) return null;
  for (const part of linkHeader.split(",")) {
    const m = part.match(/<([^>]+)>\s*;\s*rel\s*=\s*"?next"?/i);
    if (!m) continue;
    try {
      // The header's URL is absolute in practice; the base is only a fallback
      // for a relative one, and is read defensively so this parses outside a
      // browser too (tests).
      return new URL(m[1], globalThis.location?.origin).searchParams.get("max_id");
    } catch {
      return null;
    }
  }
  return null;
}

// Cursor for the page after the one just returned.
//
// A Mastodon-style API advertises the next page in a Link rel="next" header
// and omits the header entirely once there are none left, so its presence is
// an exact "there is more" signal — better than guessing from the page
// length. Callers get both: `hasLinkNext` says the server told us there's
// another page, `nextMaxId` is the cursor to ask for it.
//
// When the header isn't readable (a server that doesn't expose it via CORS)
// hasLinkNext is false and the cursor falls back to the oldest status's id,
// which is what every timeline here paginates by anyway. Collection endpoints
// (/favourites, /bookmarks) page by a record id that isn't on the statuses, so
// they pass linkOnly and simply stop when the header is missing.
function pageCursor(response, page, { linkOnly = false } = {}) {
  const linkMaxId = parseLinkNextMaxId(response.headers?.get("Link"));
  const oldest = page.length ? page[page.length - 1].id : null;
  return {
    hasLinkNext: !!linkMaxId,
    nextMaxId: linkMaxId || (linkOnly ? null : oldest),
  };
}

// Fetch one timeline page. `maxId` requests the next (older) page via the
// API's cursor. Returns { statuses, nextMaxId, pageSize, hasLinkNext } where
// pageSize is the raw page length (before any ancestor hydration) so the
// caller can tell whether a full page came back, nextMaxId is the cursor for
// the page after this one, and hasLinkNext reports whether the server
// advertised that next page (see pageCursor).

// Boards whose catalog collapses a flat timeline into OP threads, and so needs
// the OPs the page omitted. Without hydration those threads have no card at
// all: a measured /local/ page turned 40 statuses into 10 cards, silently
// dropping the other 30 because their OP fell outside the window. /home/ has
// always hydrated; the two public boards now do too, since batching made it
// cheap.
//
// Deliberately excluded: the collection boards (/likes/, /bkmks/, /dm/) render
// every status as its own card, so hydrated ancestors would show up as cards
// nobody liked or bookmarked. Profiles are excluded for the same reason —
// their catalog is filtered to the account's own posts anyway.
const HYDRATED_BOARDS = new Set(["home", "twkn", "local"]);

export async function loadTimeline(board, state, { maxId, includeReplies = false } = {}) {
  // /dm/ has two scopes: conversation previews (the default) and every direct
  // message (state.dmAllMessages). They're different endpoints with different
  // response shapes and cursors — see DM_ALL_MESSAGES_PATH.
  const dmAll = board === "dm" && !!state.dmAllMessages;
  const base = dmAll ? DM_ALL_MESSAGES_PATH : LIVE_BOARD_PATHS[board];
  if (!base) throw new Error(`no live path for board "${board}"`);
  const r = await apiFetch(appendMaxId(base, maxId), {}, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw new Error(`timeline fetch failed: HTTP ${r.status}`);
  const body = await r.json();
  const page = Array.isArray(body) ? body : [];
  const pageSize = page.length;
  // Collection boards (/favourites, /bookmarks, /dm/'s conversation scope)
  // paginate by the record id carried in the Link header, not the oldest
  // status's id. The direct timeline is an ordinary status timeline.
  const isCollection = board === "likes" || board === "bkmks" || (board === "dm" && !dmAll);
  const { nextMaxId, hasLinkNext } = pageCursor(r, page, { linkOnly: isCollection });
  let statuses = page;
  if (HYDRATED_BOARDS.has(board) && !includeReplies) {
    statuses = await hydrateMissingThreadAncestors(page, state.auth.accessToken, state.auth.apiBase);
  } else if (dmAll) {
    statuses = page.map(withDirectParticipants);
  } else if (board === "dm") {
    statuses = conversationsToStatuses(page);
  }
  return { statuses, nextMaxId, pageSize, hasLinkNext };
}

// Give a direct-timeline status the same `conversation_accounts` the
// conversation scope gets from the API, so both scopes render the same "who
// is this with" subject line: the message's author plus everyone it mentions.
function withDirectParticipants(status) {
  if (!status?.id) return status;
  const accounts = [];
  const seen = new Set();
  for (const account of [status.account, ...(status.mentions || [])]) {
    const key = account?.id || account?.acct || "";
    if (!account || !key || seen.has(key)) continue;
    seen.add(key);
    accounts.push(account);
  }
  return { ...status, conversation_accounts: accounts };
}

// /dm/ is fed by /api/v1/conversations, which returns Conversation entities
// ({ id, unread, accounts[], last_status }) rather than statuses. The catalog
// speaks statuses, so each conversation contributes its last_status carrying
// the conversation id and its participants — the card shows who the DM is
// with, and clicking through opens the conversation like any other thread.
// Conversations whose last_status the server withheld are skipped.
function conversationsToStatuses(page) {
  const statuses = [];
  for (const conversation of page) {
    const last = conversation?.last_status;
    if (!last?.id) continue;
    statuses.push({
      ...last,
      conversation_id: conversation.id,
      conversation_accounts: Array.isArray(conversation.accounts) ? conversation.accounts : [],
    });
  }
  return statuses;
}

// Fetch one page of an account's statuses for the /user/ profile view. Mirrors
// loadTimeline's cursor contract: the account-statuses endpoint paginates by
// `max_id` of the status id, exactly like the timelines, so the same
// nextMaxId/pageSize machinery drives infinite scroll.
//
// includeReplies explicitly requests replies and reblogs (exclude_replies=
// exclude_reblogs=false). The Mastodon default already includes them, but the
// two params are passed explicitly so the flat profile catalog ("show replies
// and reposts" toggle) reliably sees them regardless of a server's defaults.
export async function loadAccountStatuses(accountId, state, { maxId, includeReplies } = {}) {
  let base = `/api/v1/accounts/${accountId}/statuses?limit=${TIMELINE_PAGE_SIZE}`;
  if (includeReplies) base += "&exclude_replies=false&exclude_reblogs=false";
  const r = await apiFetch(appendMaxId(base, maxId), {}, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw new Error(`profile fetch failed: HTTP ${r.status}`);
  const page = await r.json();
  const statuses = Array.isArray(page) ? page : [];
  const pageSize = statuses.length;
  const { nextMaxId, hasLinkNext } = pageCursor(r, statuses);
  return { statuses, nextMaxId, pageSize, hasLinkNext };
}

// An account's pinned ("featured") posts — the whole collection, unpaged,
// which is what every server that implements it returns: the limits are small
// (Mastodon 5, Mitra and Pleroma configurable) and the collection is a
// deliberate curation, not a feed.
//
// Fetched separately rather than read off the account timeline, because the
// `pinned` flag on a status is only reliable in this collection: Mastodon
// documents it as appearing "only if the status is pinnable by you", so
// someone else's pinned posts are indistinguishable in their ordinary
// timeline.
export async function loadPinnedStatuses(accountId, state) {
  const r = await apiFetch(
    `/api/v1/accounts/${encodeURIComponent(accountId)}/statuses?pinned=true&limit=40`,
    {}, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw new Error(`pinned fetch failed: HTTP ${r.status}`);
  const page = await r.json();
  return Array.isArray(page) ? page : [];
}

// Render a post body without posting it. Mitra has a dedicated endpoint for
// this, which is the only safe kind: Pleroma's equivalent is the *create*
// endpoint with `preview: true`, and a server that ignored the flag would
// publish the draft. Nothing here ever calls create.
//
// Returns { content, emojis } — the server's own HTML, so a markdown dialect
// we can't reproduce locally (Mitra's comrak: greentext, hard breaks) shows
// exactly as it will once posted. Throws when the server has no such endpoint;
// the caller falls back to rendering locally.
export async function previewStatus(status, contentType, state) {
  const r = await apiFetch("/api/v1/statuses/preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, content_type: contentType }),
  }, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) {
    const err = new Error(`preview failed: HTTP ${r.status}`);
    err.status = r.status;
    throw err;
  }
  const data = await r.json();
  return { content: data?.content || "", emojis: Array.isArray(data?.emojis) ? data.emojis : [] };
}

// Pin or unpin one of your own posts. Both servers require it to be yours and
// public (mitra: `post.is_public()`, pleroma: `activity_is_public`), and both
// answer with the updated Status.
export async function setStatusPinned(statusId, pinned, state) {
  const path = `/api/v1/statuses/${encodeURIComponent(statusId)}/${pinned ? "pin" : "unpin"}`;
  const r = await apiFetch(path, { method: "POST" }, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    let detail = "";
    try { detail = JSON.parse(text).error || ""; } catch {}
    throw new Error(detail || `HTTP ${r.status}`);
  }
  return r.json();
}

// Fetch one page of a group's timeline (custom boards). Mitra's group
// timeline returns top-level posts only (replies stay in /context), newest
// first, paged by `max_id` of the post id — the same cursor contract as the
// other timelines. Requires auth.
export async function loadGroupTimeline(groupId, state, { maxId } = {}) {
  const base = `/api/v1/timelines/group/${encodeURIComponent(groupId)}?limit=${TIMELINE_PAGE_SIZE}`;
  const r = await apiFetch(appendMaxId(base, maxId), {}, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw new Error(`board fetch failed: HTTP ${r.status}`);
  const page = await r.json();
  const statuses = Array.isArray(page) ? page : [];
  const pageSize = statuses.length;
  const { nextMaxId, hasLinkNext } = pageCursor(r, statuses);
  return { statuses, nextMaxId, pageSize, hasLinkNext };
}

// Public hashtag timeline. Hydrate missing reply ancestors just like /home/
// so a tagged reply can still bump and resolve to its root catalog thread.
export async function loadTagTimeline(tag, state, { maxId } = {}) {
  const base = `/api/v1/timelines/tag/${encodeURIComponent(tag)}?limit=${TIMELINE_PAGE_SIZE}`;
  const r = await apiFetch(appendMaxId(base, maxId), {}, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw new Error(`hashtag timeline failed: HTTP ${r.status}`);
  const page = await r.json();
  const raw = Array.isArray(page) ? page : [];
  const pageSize = raw.length;
  const { nextMaxId, hasLinkNext } = pageCursor(r, raw);
  const statuses = await hydrateMissingThreadAncestors(raw, state.auth.accessToken, state.auth.apiBase);
  return { statuses, nextMaxId, pageSize, hasLinkNext };
}

// Groups the signed-in user follows (Mitra groups are accounts with
// is_group: true). These become the custom boards in the nav.
// `filter` is "following" (default) or "moderating" (boards the user admins).
export async function loadFollowedGroups(state, filter = "following") {
  const r = await apiFetch(
    `/api/v1/groups/followed?filter=${encodeURIComponent(filter)}&limit=100`,
    {}, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) {
    // The status rides along so the caller can tell "this server has no group
    // API" from "that request happened to fail" — one is permanent, the other
    // must not make the boards disappear.
    const err = new Error(`groups fetch failed: HTTP ${r.status}`);
    err.status = r.status;
    throw err;
  }
  const groups = await r.json();
  return Array.isArray(groups) ? groups : [];
}

// Shared error unwrap for the group management endpoints — Mitra returns
// {error, error_description} JSON on validation failures.
async function groupError(r) {
  const text = await r.text().catch(() => "");
  let detail = text;
  try { detail = JSON.parse(text).error_description || JSON.parse(text).error || text; } catch {}
  return new Error(detail ? String(detail).slice(0, 200) : `HTTP ${r.status}`);
}

// Create a group (custom board). The server validates `name` as a local
// username and auto-follows the creator. Returns the group's Account.
export async function createGroup(name, description, state) {
  const body = { name };
  if (description) body.description = description;
  const r = await apiFetch("/api/v1/groups", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw await groupError(r);
  return r.json();
}

// Source (markdown) form of a group's description, for editing. Admin-only.
export async function getGroupSource(groupId, state) {
  const r = await apiFetch(`/api/v1/groups/${encodeURIComponent(groupId)}/source`, {}, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw await groupError(r);
  return r.json();   // { description: string|null }
}

// Update a group's description (the only editable field). Admin-only.
// Returns the group's updated Account.
export async function updateGroup(groupId, description, state) {
  const r = await apiFetch(`/api/v1/groups/${encodeURIComponent(groupId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ description }),
  }, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw await groupError(r);
  return r.json();
}

// Delete a group (custom board). Admin-only; the server responds 204.
export async function deleteGroup(groupId, state) {
  const r = await apiFetch(`/api/v1/groups/${encodeURIComponent(groupId)}`, {
    method: "DELETE",
  }, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw await groupError(r);
}

// Run /api/v2/search and return { accounts, statuses, hashtags }. Without a
// type, Mitra chooses one category based on query syntax; Mastodon searches all.
// `resolve=true` makes Mitra fetch remote profiles/statuses by handle or URL
// that aren't cached yet (the whole point of a federated search). Requires
// auth. limit caps each type's page; offset pages a specified result type.
export async function searchAll(query, state, { type, limit = 20, offset = 0 } = {}) {
  const params = new URLSearchParams();
  params.set("q", query);
  params.set("limit", String(limit));
  params.set("resolve", "true");
  if (type) params.set("type", type);   // "accounts" | "statuses" | "hashtags"
  if (offset) params.set("offset", String(offset));
  const r = await apiFetch(`/api/v2/search?${params.toString()}`, {}, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw new Error(`search failed: HTTP ${r.status}`);
  const j = await r.json();
  return {
    accounts: Array.isArray(j.accounts) ? j.accounts : [],
    statuses: Array.isArray(j.statuses) ? j.statuses : [],
    hashtags: Array.isArray(j.hashtags) ? j.hashtags : [],
  };
}

// Search tabs must not rely on Mitra's untyped query classification: a word
// like "LLM" is an account query there, not a full-text post search.
export async function searchByType(query, state, { type, limit = 20, offset = 0 } = {}) {
  let q = query.trim();
  let requestType = type;
  const objectLookup = /^(?:https?:\/\/|ap:|did:|0x[0-9a-f]{40}$)/i.test(q);
  if (objectLookup || (type === "accounts" && /^@?[^\s@]+@[^\s@]+$/.test(q))) {
    // Mitra's untyped route imports exact remote handles and object URLs.
    // Keep that behavior instead of searching the URL's text in post bodies.
    if (type === "hashtags") return { accounts: [], statuses: [], hashtags: [] };
    requestType = undefined;
  } else if (type === "statuses") {
    q = q.replace(/^>\s*/, ""); // accept Mitra's existing full-text syntax
  } else if (type === "hashtags") {
    // Mitra currently falls back to its untyped parser for type=hashtags.
    q = `#${q.replace(/^#/, "")}`;
  }
  return searchAll(q, state, { type: requestType, limit, offset });
}

export async function loadInstance(state) {
  const r = await apiFetch("/api/v1/instance", {}, state.auth.accessToken, state.auth.apiBase);
  if (!r.ok) throw new Error(`instance fetch failed: HTTP ${r.status}`);
  return r.json();
}

// Single status by id, through the same session-lifetime cache the catalog
// hydration path uses. Returns null when the status is gone or unreadable.
export function fetchStatus(id, state) {
  return fetchStatusById(id, state?.auth?.accessToken, state?.auth?.apiBase);
}

export async function fetchContext(id, state) {
  const [postR, ctxR] = await Promise.all([
    apiFetch(`/api/v1/statuses/${id}`, {}, state.auth.accessToken, state.auth.apiBase),
    apiFetch(`/api/v1/statuses/${id}/context`, {}, state.auth.accessToken, state.auth.apiBase),
  ]);
  if (!postR.ok) throw new Error(`status fetch failed: HTTP ${postR.status}`);
  const op = await postR.json();
  let ctx = { ancestors: [], descendants: [] };
  if (ctxR.ok) ctx = await ctxR.json();
  return { ...ctx, op };
}
